/**
 * 单位转换工具模块
 * 
 * 本模块提供了毫米（mm）与像素（px）之间的转换功能。
 * 主要用于将模板的物理尺寸（毫米）转换为 canvas 的像素尺寸。
 * 转换基于 DPI（每英寸点数）进行计算。
 */

import type { CanvasSize } from './types';

/**
 * 毫米到英寸的转换系数
 * 1 英寸 = 25.4 毫米，因此 1 毫米 = 1/25.4 英寸
 */
const MM_TO_INCH = 1 / 25.4;

/**
 * 单位转换工具类
 * 
 * 提供静态方法用于毫米和像素之间的相互转换。
 */
export class UnitConverter {
    /**
     * 将毫米转换为像素
     * 
     * 计算公式：像素 = 毫米 × (1/25.4) × DPI
     * 
     * @param mm - 毫米值
     * @param dpi - DPI（每英寸点数）
     * @returns 像素值（四舍五入为整数）
     */
    static mmToPixels(mm: number, dpi: number): number {
        return Math.round(mm * MM_TO_INCH * dpi);
    }

    /**
     * 将像素转换为毫米
     * 
     * 计算公式：毫米 = 像素 / DPI / (1/25.4)
     * 
     * @param pixels - 像素值
     * @param dpi - DPI（每英寸点数）
     * @returns 毫米值
     */
    static pixelsToMm(pixels: number, dpi: number): number {
        return pixels / dpi / MM_TO_INCH;
    }

    /**
     * 计算 canvas 尺寸
     * 
     * 根据模板的物理尺寸（毫米）和 DPI，计算出 canvas 的像素尺寸。
     * 
     * @param widthMm - 宽度（毫米）
     * @param heightMm - 高度（毫米）
     * @param dpi - DPI（每英寸点数）
     * @returns Canvas 尺寸对象，包含宽度和高度（像素）
     */
    static calculateCanvasSize(widthMm: number, heightMm: number, dpi: number): CanvasSize {
        return {
            width: UnitConverter.mmToPixels(widthMm, dpi),
            height: UnitConverter.mmToPixels(heightMm, dpi)
        };
    }
}
